import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  Target,
  Sparkles,
  Trophy,
  Dumbbell,
  ClipboardCheck,
  Timer,
  ArrowRight,
  ChevronDown,
  Calculator,
  BookOpen,
  TestTube2,
  Landmark,
} from "lucide-react";
import { avanzoApi } from "@/lib/avanzoApi";

const SUBJECT_LABELS = {
  matematica: "Matemática",
  lenguaje: "Lenguaje",
  ciencias: "Ciencia",
  sociales: "Ciudadanía",
};
const SUBJECT_VAR = {
  matematica: "var(--avz-subject-matematica)",
  lenguaje:   "var(--avz-subject-lenguaje)",
  ciencias:   "var(--avz-subject-ciencias)",
  sociales:   "var(--avz-subject-sociales)",
};
const SUBJECT_ICON = {
  matematica: Calculator,
  lenguaje:   BookOpen,
  ciencias:   TestTube2,
  sociales:   Landmark,
};

const MOTIVATIONS = {
  hero:      "Disciplina hoy, más oportunidades mañana",
  continue:  "Grandes resultados comienzan con pequeños pasos",
  footer:    "La preparación de hoy abre las puertas de mañana",
};

function firstName(fullName) {
  return String(fullName || "").trim().split(/\s+/)[0];
}
function pctFromMastery(m) {
  return Math.max(0, Math.min(100, Math.round(Number(m || 0) * 100)));
}
function statusForMastery(m, hasData) {
  if (!hasData) return "Comienza aquí";
  const p = pctFromMastery(m);
  if (p >= 75) return "Buen progreso";
  if (p >= 60) return "Sigue practicando";
  if (p >= 40) return "Vas por buen camino";
  return "Tú puedes";
}

/* ══════════════════════════════════════════════════════════════════
 * TopHeader
 * ══════════════════════════════════════════════════════════════════ */
function TopHeader({ summary }) {
  const streak = Number(summary?.streak || 0);
  const attempts = Number(summary?.attempts_total || 0);
  const skillsStrong = (summary?.subjects || []).reduce(
    (acc, s) => acc + (s.skills_strong?.length || 0), 0,
  );
  const name = summary?.student_name || "Estudiante";
  const initials = (name || "?").trim().split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase() || "AV";

  return (
    <header className="avz-topbar" data-testid="avz-topbar">
      <div className="avz-topbar-metrics">
        <div className="avz-metric" data-testid="avz-metric-streak">
          <span className="avz-metric-ico avz-metric-ico--amber" aria-hidden="true">
            <Flame size={14} />
          </span>
          <div className="avz-metric-text">
            <span className="avz-metric-label">Racha</span>
            <span className="avz-metric-value" data-testid="avz-metric-streak-value">
              {streak} {streak === 1 ? "día" : "días"}
            </span>
          </div>
        </div>
        <div className="avz-metric" data-testid="avz-metric-attempts">
          <span className="avz-metric-ico avz-metric-ico--blue" aria-hidden="true">
            <Sparkles size={14} />
          </span>
          <div className="avz-metric-text">
            <span className="avz-metric-label">Preguntas resueltas</span>
            <span className="avz-metric-value" data-testid="avz-metric-attempts-value">{attempts}</span>
          </div>
        </div>
        <div className="avz-metric" data-testid="avz-metric-strong">
          <span className="avz-metric-ico avz-metric-ico--teal" aria-hidden="true">
            <Trophy size={14} />
          </span>
          <div className="avz-metric-text">
            <span className="avz-metric-label">Habilidades dominadas</span>
            <span className="avz-metric-value" data-testid="avz-metric-strong-value">{skillsStrong}</span>
          </div>
        </div>
      </div>

      <div className="avz-topbar-user" data-testid="avz-topbar-user">
        <div className="avz-avatar" aria-hidden="true">{initials}</div>
        <div className="avz-topbar-user-text">
          <span className="avz-topbar-user-name">Hola, {firstName(name)}</span>
          <span className="avz-topbar-user-sub">Estudiante</span>
        </div>
        <ChevronDown size={14} className="avz-topbar-user-caret" aria-hidden="true" />
      </div>
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Hero — mountain SVG with flag + hiker + handwritten scribble
 * ══════════════════════════════════════════════════════════════════ */
function Hero({ studentName }) {
  return (
    <section className="avz-hero" data-testid="avz-hero">
      <div className="avz-hero-text">
        <p className="avz-hero-hello" data-testid="avz-hero-greeting">
          Hola, {firstName(studentName) || "Estudiante"}
        </p>
        <h1 className="avz-hero-title">Hoy vamos por un paso más.</h1>
        <p className="avz-hero-subtitle">
          Cada pregunta te acerca a tus metas <span className="avz-hero-diamond">✦</span>
        </p>
      </div>
      <div className="avz-hero-visual" aria-hidden="true">
        <img
          src="/hero/hero-mountains.jpg"
          alt=""
          className="avz-hero-img"
          data-testid="avz-hero-image"
          loading="lazy"
        />
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Overall preparation — full-width card
 * ══════════════════════════════════════════════════════════════════ */
function OverallCard({ summary }) {
  const subjects = summary?.subjects || [];
  const withData = subjects.filter(s => (s.skills_practiced || 0) > 0);
  const avg = withData.length
    ? withData.reduce((acc, s) => acc + Number(s.avg_mastery || 0), 0) / withData.length
    : 0;
  const pct = pctFromMastery(avg);
  const hasData = withData.length > 0;
  const daysUntil = summary?.days_until_avanzo;

  return (
    <section className="avz-overall" data-testid="avz-overall-progress">
      <div className="avz-overall-main">
        <div className="avz-overall-head">
          <h3 className="avz-overall-title">Tu preparación para AVANZO</h3>
          {daysUntil != null && (
            <span className="avz-overall-days" data-testid="avz-days-until">
              {daysUntil} <em>días</em>
            </span>
          )}
        </div>
        <div className="avz-overall-progress-row">
          <div
            className="avz-bar avz-bar--lg"
            role="progressbar"
            aria-label="Progreso general"
            aria-valuenow={hasData ? pct : 0}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="avz-overall-bar"
          >
            <div className="avz-bar-fill avz-bar-fill--gradient" style={{ width: `${hasData ? Math.max(6, pct) : 4}%` }} />
          </div>
          <span className="avz-overall-pct" data-testid="avz-overall-pct">
            {hasData ? `${pct}%` : "—"}
          </span>
        </div>
        <p className="avz-overall-caption avz-mute">
          {hasData
            ? "Estás construyendo un mejor futuro. ¡Sigue así!"
            : "Aún no tenemos suficiente información. Empieza a entrenar para ver tu avance."}
        </p>
      </div>
      <div className="avz-goal" data-testid="avz-goal-card">
        <div className="avz-goal-icon" aria-hidden="true"><Trophy size={18} /></div>
        <div>
          <div className="avz-goal-eyebrow">Tu meta</div>
          <div className="avz-goal-text">Completar tu preparación para AVANZO</div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Continue — subject icon + progress + scribble mountain
 * ══════════════════════════════════════════════════════════════════ */
function ContinueCard({ today, summary }) {
  const materia = today?.materia;
  const habilidad = today?.habilidad;
  const materiaLabel = materia ? (SUBJECT_LABELS[materia] || materia) : "Empieza donde prefieras";
  const materiaColor = materia ? SUBJECT_VAR[materia] || "var(--avz-brand)" : "var(--avz-brand)";
  const Icon = materia ? (SUBJECT_ICON[materia] || Calculator) : Calculator;

  // Mastery of chosen subject for the mini progress bar
  const subjEntry = (summary?.subjects || []).find(s => s.key === materia);
  const subjPct = subjEntry ? pctFromMastery(subjEntry.avg_mastery) : 0;
  const hasSubjData = !!(subjEntry && subjEntry.skills_practiced > 0);

  return (
    <section className="avz-continue" data-testid="avz-continue">
      <div className="avz-continue-icon" style={{ background: materiaColor }} aria-hidden="true">
        <Icon size={30} strokeWidth={2.2} color="#fff" />
      </div>
      <div className="avz-continue-body">
        <div className="avz-eyebrow">Continúa donde quedaste</div>
        <h3 className="avz-continue-title" data-testid="avz-continue-subject">{materiaLabel}</h3>
        <div className="avz-continue-sub avz-mute">Progreso en esta área</div>
        <div className="avz-continue-progress-row">
          <div
            className="avz-bar"
            role="progressbar"
            aria-valuenow={subjPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="avz-bar-fill"
              style={{
                width: `${hasSubjData ? Math.max(6, subjPct) : 4}%`,
                background: materiaColor,
              }}
            />
          </div>
          <span className="avz-continue-pct">{hasSubjData ? `${subjPct}%` : "—"}</span>
        </div>
        {habilidad && (
          <div className="avz-continue-next">
            <span className="avz-mute">Próximo objetivo</span>
            <span className="avz-continue-next-skill" data-testid="avz-continue-skill">{habilidad}</span>
          </div>
        )}
        <div className="avz-continue-actions">
          <Link to="/avanzo/entrenar" className="avz-btn avz-btn-primary" data-testid="avz-continue-cta">
            Continuar entrenando <ArrowRight size={16} aria-hidden="true" />
          </Link>
          <Link to="/avanzo/entrenar" className="avz-btn-link" data-testid="avz-continue-change">
            Cambiar área
          </Link>
        </div>
      </div>
      <div className="avz-continue-visual" aria-hidden="true">
        <img
          src="/hero/continue-mountain-night.jpg"
          alt=""
          className="avz-continue-img"
          data-testid="avz-continue-image"
          loading="lazy"
        />
        <div className="avz-continue-overlay" aria-hidden="true" />
        <div className="avz-continue-scribble">{MOTIVATIONS.continue}</div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * 3 acciones principales
 * ══════════════════════════════════════════════════════════════════ */
function ActionCards({ summary }) {
  const pcUnlocked = !!summary?.progress_check?.unlocked;
  const pcSessions = Number(summary?.progress_check?.sessions || 0);
  const pcThreshold = Number(summary?.progress_check?.threshold || 4);
  const finalAllowed = !!summary?.final_simulation_status?.allowed;
  const skillsStrong = (summary?.subjects || []).reduce((a, s) => a + (s.skills_strong?.length || 0), 0);

  const cards = [
    {
      testid: "avz-action-entrena",
      title: "Entrena",
      desc: "Practica por áreas y mejora donde más lo necesitas.",
      icon: Dumbbell,
      accent: "green",
      state: skillsStrong > 0
        ? { label: `${skillsStrong} habilidad${skillsStrong === 1 ? "" : "es"} reforzada${skillsStrong === 1 ? "" : "s"}`, tone: "ok" }
        : { label: "Disponible", tone: "ok" },
      cta: "Ir a entrenamiento",
      to: "/avanzo/entrenar",
    },
    {
      testid: "avz-action-progress-check",
      title: "Progress Check",
      desc: "Descubre cuánto has avanzado en cada área.",
      icon: ClipboardCheck,
      accent: "amber",
      state: pcUnlocked
        ? { label: "Disponible", tone: "ok" }
        : { label: `Faltan ${Math.max(0, pcThreshold - pcSessions)} sesiones`, tone: "muted" },
      cta: pcUnlocked ? "Comenzar ahora" : "Ver requisitos",
      to: pcUnlocked ? "/avanzo/control-progreso" : "/avanzo/entrenar",
    },
    {
      testid: "avz-action-simulacro",
      title: "Simula la prueba",
      desc: "Pon a prueba tu preparación en condiciones similares a AVANZO.",
      icon: Timer,
      accent: "purple",
      state: finalAllowed
        ? { label: "Disponible", tone: "ok" }
        : { label: "Disponible pronto", tone: "muted" },
      cta: finalAllowed ? "Iniciar simulacro" : "Ver requisitos",
      to: "/avanzo/simulacro",
    },
    {
      testid: "avz-action-practice",
      title: "Práctica",
      desc: "Practica por materia o habilidad — sesiones cortas de 5 o 10 preguntas.",
      icon: Sparkles,
      accent: "blue",
      state: { label: "Disponible", tone: "ok" },
      cta: "Ir a Práctica",
      to: "/avanzo/practica",
    },
  ];

  return (
    <section className="avz-actions" data-testid="avz-actions">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <article key={c.testid} className={`avz-action avz-action--${c.accent}`} data-testid={c.testid}>
            <div className="avz-action-icon" aria-hidden="true">
              <Icon size={24} strokeWidth={2.2} />
            </div>
            <h3 className="avz-action-title">{c.title}</h3>
            <p className="avz-action-desc avz-mute">{c.desc}</p>
            <span className={`avz-pill avz-pill--${c.state.tone}`} data-testid={`${c.testid}-state`}>
              {c.state.label}
            </span>
            <Link to={c.to} className={`avz-action-cta avz-action-cta--${c.accent}`} data-testid={`${c.testid}-cta`}>
              {c.cta} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </article>
        );
      })}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Avance por área — 4 dials
 * ══════════════════════════════════════════════════════════════════ */
function CircularProgress({ value, color, hasData }) {
  const size = 96;
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const shown = hasData ? Math.max(0.02, Math.min(1, value)) : 0;
  const offset = c - c * shown;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="avz-dial">
      <circle cx={size/2} cy={size/2} r={r} stroke="#eef2f6" strokeWidth={stroke} fill="none" />
      {hasData && (
        <circle
          cx={size/2} cy={size/2} r={r}
          stroke={color} strokeWidth={stroke} fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
      )}
      <text
        x="50%" y="52%"
        textAnchor="middle" dominantBaseline="middle"
        fontSize="20" fontWeight="800" fill="#0f1620"
      >
        {hasData ? `${Math.round(value * 100)}%` : "—"}
      </text>
    </svg>
  );
}

function AreaProgress({ summary }) {
  const subjects = summary?.subjects || [];
  const byKey = Object.fromEntries(subjects.map(s => [s.key, s]));
  const order = ["matematica", "lenguaje", "ciencias", "sociales"];
  return (
    <section className="avz-areas" data-testid="avz-areas">
      <div className="avz-section-head">
        <h2 className="avz-section-title">Tu avance por área</h2>
        <Link to="/avanzo/progreso" className="avz-section-link" data-testid="avz-areas-detail">
          Ver detalle <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
      <div className="avz-areas-grid">
        {order.map((key) => {
          const s = byKey[key];
          const label = SUBJECT_LABELS[key] || key;
          const color = SUBJECT_VAR[key];
          const hasData = !!(s && s.skills_practiced && s.skills_practiced > 0);
          const value = hasData ? Number(s.avg_mastery || 0) : 0;
          return (
            <article key={key} className="avz-area" data-testid={`avz-area-${key}`} data-subject={key}>
              <CircularProgress value={value} color={color} hasData={hasData} />
              <div className="avz-area-label">{label}</div>
              <div className="avz-area-status">{statusForMastery(value, hasData)}</div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Oportunidad + Motivational quote (right column of areas row)
 * ══════════════════════════════════════════════════════════════════ */
function OpportunityBlock({ bands }) {
  const item = bands ? (bands.practica?.items || [])[0] : null;
  const hasItem = !!(item && item.habilidad);
  const materiaLabel = hasItem ? (SUBJECT_LABELS[item.materia] || item.materia || "") : "";

  return (
    <aside className="avz-oppblock" data-testid="avz-opportunity">
      <div className="avz-opp-card">
        <div className="avz-opp-icon" aria-hidden="true"><Target size={18} /></div>
        <div>
          <div className="avz-eyebrow">Tu mayor oportunidad ahora</div>
          <div className="avz-opp-title" data-testid="avz-opportunity-title">
            {hasItem ? item.habilidad : "Sigue entrenando"}
          </div>
          <div className="avz-mute avz-mt-4" data-testid="avz-opportunity-subject">
            {hasItem
              ? `${materiaLabel} · Enfócate aquí para seguir mejorando.`
              : "Con más práctica identificaremos tu mejor oportunidad."}
          </div>
        </div>
      </div>
      <div className="avz-quote">
        <span className="avz-quote-mark" aria-hidden="true">“</span>
        <span className="avz-quote-text">{MOTIVATIONS.footer}</span>
      </div>
    </aside>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Mode banner
 * ══════════════════════════════════════════════════════════════════ */
function ModeBanner({ mode }) {
  const c = mode.copy?.home || {};
  const rec = mode.recommended_session_minutes_min;
  const recMax = mode.recommended_session_minutes_max;
  return (
    <section className="avz-mode-banner avz-mode-banner--v2" data-testid={`avz-mode-${mode.mode.toLowerCase()}`}>
      <div className="avz-h2" data-testid="avz-mode-headline">{c.headline}</div>
      {c.subtext && <p className="avz-mute avz-mt-4" data-testid="avz-mode-subtext">{c.subtext}</p>}
      {rec && recMax && (
        <p className="avz-mute avz-mt-4" data-testid="avz-mode-recommendation">
          Práctica sugerida hoy: {rec}–{recMax} minutos.
        </p>
      )}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Home root
 * ══════════════════════════════════════════════════════════════════ */
export default function AvanzoHome() {
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState(null);
  const [today, setToday] = useState(null);
  const [bands, setBands] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      avanzoApi.summary(),
      avanzoApi.trainingMode(),
      avanzoApi.paesHomeToday(),
      avanzoApi.paesProgressBands(),
    ]).then(([sumR, modeR, todayR, bandsR]) => {
      if (cancelled) return;
      if (sumR.status === "fulfilled") setSummary(sumR.value.data);
      else setError(sumR.reason);
      if (modeR.status === "fulfilled") setMode(modeR.value.data || null);
      if (todayR.status === "fulfilled") setToday(todayR.value.data || null);
      if (bandsR.status === "fulfilled") setBands(bandsR.value.data || null);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="avz-empty" data-testid="avz-home-loading">Cargando tu preparación…</div>;
  if (error || !summary) {
    return <div className="avz-empty" data-testid="avz-home-error">No pudimos cargar tu preparación. Intenta recargar.</div>;
  }

  return (
    <div className="avz-home" data-testid="avz-home">
      <TopHeader summary={summary} />
      {mode && mode.mode !== "NORMAL" && <ModeBanner mode={mode} />}
      <Hero studentName={summary.student_name} />
      <OverallCard summary={summary} />
      <ContinueCard today={today} summary={summary} />
      <ActionCards summary={summary} />
      <section className="avz-bottom-row">
        <AreaProgress summary={summary} />
        <OpportunityBlock bands={bands} />
      </section>
    </div>
  );
}
